import React from 'react';
import { Box, TextField } from '@mui/material';
import { SPACE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState } from '../presentationSettings';
import { AdminLabeledControl } from './AdminInfoLabel';
import AdminSettingCard from './AdminSettingCard';
import AdminVisibilitySwitch from './AdminVisibilitySwitch';
import { adminFieldSx } from './adminFormSx';

interface AdminLanguagesSectionProps {
  draft: PresentationSettingsState;
  setDraft: React.Dispatch<React.SetStateAction<PresentationSettingsState>>;
}

const AdminLanguagesSection: React.FC<AdminLanguagesSectionProps> = ({ draft, setDraft }) => {
  const visibleLanguages = draft.languages.filter((item) => item.visible).length;
  const visibleMarkets = draft.markets.filter((item) => item.visible).length;

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: `${SPACE[5]}px`,
      maxWidth: 920,
    }}
    >
      <AdminSettingCard title={COPY.settingsLanguagesCard} description={COPY.settingsLocaleHiddenHelp}>
        {draft.languages.map((item, index) => {
          const lastVisible = item.visible && visibleLanguages <= 1;
          return (
            <Box key={item.id} sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '80px 1fr' },
              gap: 1.5,
              alignItems: 'center',
              mb: 2,
            }}
            >
              <AdminLabeledControl label={COPY.settingsLanguageCode}>
                {({ id }) => (
                  <TextField id={id} size="small" value={item.id} disabled sx={adminFieldSx()} />
                )}
              </AdminLabeledControl>
              <AdminLabeledControl label={COPY.settingsLanguageLabel}>
                {({ id }) => (
                  <TextField
                    id={id}
                    size="small"
                    fullWidth
                    value={item.label}
                    onChange={(event) => {
                      const languages = draft.languages.map((row, i) => (
                        i === index ? { ...row, label: event.target.value } : row
                      ));
                      setDraft((current) => ({ ...current, languages }));
                    }}
                    sx={adminFieldSx()}
                  />
                )}
              </AdminLabeledControl>
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <AdminVisibilitySwitch
                  checked={item.visible}
                  disabled={lastVisible}
                  disabledReason={lastVisible ? COPY.settingsLastVisibleLocale : undefined}
                  label={COPY.settingsVisible}
                  onChange={(checked) => {
                    const languages = draft.languages.map((row, i) => (
                      i === index ? { ...row, visible: checked } : row
                    ));
                    setDraft((current) => ({ ...current, languages }));
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </AdminSettingCard>

      <AdminSettingCard title={COPY.settingsMarkets} description={COPY.settingsLocaleHiddenHelp}>
        {draft.markets.map((item, index) => {
          const lastVisible = item.visible && visibleMarkets <= 1;
          return (
            <Box key={item.id} sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '80px 1fr' },
              gap: 1.5,
              alignItems: 'center',
              mb: 2,
            }}
            >
              <AdminLabeledControl label={COPY.settingsLanguageCode}>
                {({ id }) => (
                  <TextField id={id} size="small" value={item.id} disabled sx={adminFieldSx()} />
                )}
              </AdminLabeledControl>
              <AdminLabeledControl label={COPY.settingsLanguageLabel}>
                {({ id }) => (
                  <TextField
                    id={id}
                    size="small"
                    fullWidth
                    value={item.label}
                    onChange={(event) => {
                      const markets = draft.markets.map((row, i) => (
                        i === index ? { ...row, label: event.target.value } : row
                      ));
                      setDraft((current) => ({ ...current, markets }));
                    }}
                    sx={adminFieldSx()}
                  />
                )}
              </AdminLabeledControl>
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <AdminVisibilitySwitch
                  checked={item.visible}
                  disabled={lastVisible}
                  disabledReason={lastVisible ? COPY.settingsLastVisibleLocale : undefined}
                  label={COPY.settingsVisible}
                  onChange={(checked) => {
                    const markets = draft.markets.map((row, i) => (
                      i === index ? { ...row, visible: checked } : row
                    ));
                    setDraft((current) => ({ ...current, markets }));
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </AdminSettingCard>
    </Box>
  );
};

export default AdminLanguagesSection;
